import type { CampaignAnalyticsEpoch, CampaignAnalyticsEpochGroup } from "@cede/types";

/**
 * Rebuilds a campaign's epoch list from its epoch groups.
 *
 * An epoch row is generated from its group at a fixed cadence, so a campaign with a thousand
 * epochs is fully described by a handful of groups. Sending the groups and deriving here keeps the
 * analytics payload proportional to the group count, which grows only when the campaign is
 * reconfigured, rather than to the epoch count, which grows every cadence tick.
 *
 * Ids are assigned as a running counter across groups ordered by start, matching how the epoch
 * rows were written. Callers must check the result against the campaign's stored epoch count —
 * a campaign whose rows were edited outside that generation is not derivable, and the difference
 * has to surface rather than render a plausible-looking but wrong list.
 *
 * The cadence is absolute milliseconds, so the group bounds must be the same instants Prisma
 * produced: the columns are `timestamp without time zone`, and a reader that resolves them in a
 * local zone instead shifts a window that spans a DST change by an hour, which turns into a
 * spurious extra epoch at the end of the group.
 */
export function deriveCampaignEpochs(groups: CampaignAnalyticsEpochGroup[]): CampaignAnalyticsEpoch[] {
  const ordered = [...groups].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const epochs: CampaignAnalyticsEpoch[] = [];

  for (const group of ordered) {
    const start = new Date(group.start);
    const end = new Date(group.end);
    const shared = {
      exchangeIds: group.exchangeIds,
      expectedTradedVolume: group.expectedTradedVolume,
    };

    // A group without a cadence is a single epoch spanning its whole window.
    if (!group.durationMs) {
      epochs.push({ id: epochs.length, start, end, ...shared });
      continue;
    }

    for (let cursor = start; cursor < end; ) {
      const next = new Date(Math.min(cursor.getTime() + group.durationMs, end.getTime()));
      epochs.push({ id: epochs.length, start: cursor, end: next, ...shared });
      cursor = next;
    }
  }

  return epochs;
}
